import { Fragment, useRef } from 'react'

import { flexRender } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import { cn } from '@/utils/cn'

import type { TableBodyProps, VirtualizationConfig } from '../DataTable.types'
import { getAlignmentClass, getStickyColumnClass, getStickyColumnStyles } from '../DataTable.utils'

export const TableBody = <TData,>({
  table,
  onRowClick,
  renderSubRow,
  renderRowActions,
  virtualization = false,
  className
}: TableBodyProps<TData>) => {
  const rows = table.getRowModel().rows

  // Show empty state if no rows
  if (rows.length === 0) {
    return (
      <tbody className={className}>
        <tr>
          <td
            colSpan={table.getAllColumns().length}
            className="h-24 text-center text-muted-foreground"
          >
            No se encontraron resultados
          </td>
        </tr>
      </tbody>
    )
  }

  // Use virtualization if enabled
  if (virtualization) {
    const config = typeof virtualization === 'object' ? virtualization : {}
    return (
      <VirtualizedTableBody
        table={table}
        rows={rows}
        onRowClick={onRowClick}
        renderSubRow={renderSubRow}
        renderRowActions={renderRowActions}
        config={config}
        className={className}
      />
    )
  }

  // Regular table body without virtualization
  return (
    <tbody className={cn('[&_tr:last-child]:border-0', className)}>
      {rows.map((row) => (
        <Fragment key={row.id}>
          <TableRow
            row={row}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            renderRowActions={renderRowActions}
          />
          {row.getIsExpanded() && renderSubRow && (
            <tr className="border-b">
              <td colSpan={row.getVisibleCells().length} className="p-0">
                <div className="px-4 py-2 bg-muted/30">{renderSubRow(row)}</div>
              </td>
            </tr>
          )}
        </Fragment>
      ))}
    </tbody>
  )
}

// Virtualized table body component
// interface VirtualizedTableBodyProps<TData> {
interface VirtualizedTableBodyProps {
  table: any
  rows: any[]
  onRowClick?: (row: any) => void
  renderSubRow?: (row: any) => React.ReactNode
  renderRowActions?: (row: any) => React.ReactNode
  config: VirtualizationConfig
  className?: string
}

// const VirtualizedTableBody = <TData,>({
const VirtualizedTableBody = ({
  // table,
  rows,
  onRowClick,
  renderSubRow,
  renderRowActions,
  config,
  className
  // }: VirtualizedTableBodyProps<TData>) => {
}: VirtualizedTableBodyProps) => {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: config.estimateSize || (() => config.rowHeight || 53),
    overscan: config.overscan || 10,
    scrollMargin: config.scrollMargin || 0
  })

  const virtualRows = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0) : 0

  return (
    <div ref={parentRef} className="h-[400px] overflow-auto">
      <table className="w-full">
        <tbody className={cn('[&_tr:last-child]:border-0', className)}>
          {paddingTop > 0 && (
            <tr>
              <td style={{ height: paddingTop }} />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index]
            return (
              <Fragment key={row.id}>
                <TableRow
                  row={row}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  renderRowActions={renderRowActions}
                  style={{ height: virtualRow.size }}
                />
                {row.getIsExpanded() && renderSubRow && (
                  <tr className="border-b">
                    <td colSpan={row.getVisibleCells().length} className="p-0">
                      <div className="px-4 py-2 bg-muted/30">{renderSubRow(row)}</div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          {paddingBottom > 0 && (
            <tr>
              <td style={{ height: paddingBottom }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// Individual table row component
// interface TableRowProps<TData> {
interface TableRowProps {
  row: any
  onClick?: () => void
  renderRowActions?: (row: any) => React.ReactNode
  selected?: boolean
  style?: React.CSSProperties
}

// const TableRow = <TData,>({
const TableRow = ({
  row,
  onClick,
  renderRowActions,
  selected,
  style
  // }: TableRowProps<TData>) => {
}: TableRowProps) => {
  return (
    <tr
      className={cn(
        'border-b transition-colors',
        'hover:bg-muted/50 data-[state=selected]:bg-muted',
        onClick && 'cursor-pointer'
      )}
      onClick={onClick}
      data-state={selected ? 'selected' : undefined}
      style={style}
    >
      {row.getVisibleCells().map((cell: any) => {
        const meta = cell.column.columnDef.meta

        return (
          <TableCell
            key={cell.id}
            className={cn(
              meta?.cellClassName,
              getAlignmentClass(meta?.align),
              getStickyColumnClass(meta?.sticky)
            )}
            style={{
              width: meta?.width,
              minWidth: meta?.minWidth,
              maxWidth: meta?.maxWidth,
              ...getStickyColumnStyles(meta?.sticky)
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        )
      })}

      {renderRowActions && <TableCell className="text-right">{renderRowActions(row)}</TableCell>}
    </tr>
  )
}

// Table cell component
interface TableCellProps {
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

const TableCell: React.FC<TableCellProps> = ({ className, style, children }) => {
  return (
    <td className={cn('p-4 align-middle [&:has([role=checkbox])]:pr-0', className)} style={style}>
      {children}
    </td>
  )
}

export { TableCell, TableRow }
